import axios from 'axios'
import {ethers} from 'ethers'
import {rpc} from '@cityofzion/neon-core'
import {neoXBridgeABI} from '@/libs/x-bridge/NeoXBridgeABI'
import {BridgeNetworkHelper} from '@/helpers/BridgeNetworkHelper'

/**
 * A bridge transfer is two transactions on two chains: the one the user signs
 * (the source leg) and the one the bridge validators broadcast on the other
 * chain once they see it (the target leg). Each leg is tracked on its own.
 */
export type TBridgeLegStatus =
  | 'idle'
  | 'pending'
  | 'success'
  | 'error'
  | 'timeout'

/** The single execution of an N3 application log, as far as tracking cares. */
export type TN3Execution = {
  vmState: string
  exception: string | null
  notifications: {
    eventname: string
    state: {value: {value: string}[]}
  }[]
}

export type TN3Release = {
  transactionHash: string
  vmState: string
}

/** Returns true when the caller lost interest and polling should stop. */
type TShouldStop = () => boolean

export abstract class BridgeTrackingHelper {
  /** What the Neo X indexer reports while a deposit has not been released yet. */
  private static readonly EMPTY_TRANSACTION_HASH = `0x${'00'.repeat(32)}`

  /** A block takes ~15s on N3 and ~1s on Neo X, so the source leg is cheap to poll. */
  private static readonly SOURCE_POLL_INTERVAL_MS = 5000

  /** The target leg waits on the validators, which batch — polling it fast is wasted. */
  private static readonly TARGET_POLL_INTERVAL_MS = 10000

  /**
   * How long a leg is followed before giving up. Giving up is not a failure:
   * the funds are already committed to the bridge, only the tracking stops.
   */
  private static readonly TIMEOUT_MS = 180000

  /**
   * Waits for an N3 transaction to be executed and returns its execution.
   * `null` means it never showed up within the timeout.
   */
  static async waitForN3Execution(
    transactionHash: string,
    rpcUrl: string,
    shouldStop: TShouldStop = () => false
  ): Promise<TN3Execution | null> {
    const rpcClient = new rpc.RPCClient(rpcUrl)

    return this.poll(this.SOURCE_POLL_INTERVAL_MS, shouldStop, async () => {
      const result: any = await rpcClient.getApplicationLog(transactionHash)
      const execution = result?.executions?.[0]

      if (!execution?.vmstate) {
        return null
      }

      return {
        vmState: execution.vmstate,
        exception: execution.exception ?? null,
        notifications: execution.notifications ?? [],
      }
    })
  }

  /**
   * The nonce the N3 bridge contract assigned to the deposit. It is what the
   * Neo X side is indexed by, so without it the transfer cannot be followed.
   */
  static depositNonceOf(
    execution: TN3Execution,
    isNativeToken: boolean
  ): string | null {
    const eventName = isNativeToken ? 'NativeDeposit' : 'TokenDeposit'
    const nonceIndex = isNativeToken ? 0 : 2

    const notification = execution.notifications.find(
      ({eventname}) => eventname === eventName
    )

    return notification?.state?.value?.[nonceIndex]?.value ?? null
  }

  /**
   * Waits for the Neo X transaction that released the deposited funds.
   * `tokenHash` is the ERC-20 address on Neo X, or `null` for native GAS.
   */
  static async waitForNeoXRelease({
    nonce,
    tokenHash,
    shouldStop = () => false,
  }: {
    nonce: string
    tokenHash: string | null
    shouldStop?: TShouldStop
  }): Promise<string | null> {
    const baseUrl = `${BridgeNetworkHelper.xIndexerUrl()}/api/v1/transactions/deposits`
    const url = tokenHash
      ? `${baseUrl}/${tokenHash}/${nonce}`
      : `${baseUrl}/${nonce}`

    return this.poll(this.TARGET_POLL_INTERVAL_MS, shouldStop, async () => {
      const {data} = await axios.get<{txid?: string | null}>(url)
      const transactionHash = data?.txid

      if (!transactionHash || transactionHash === this.EMPTY_TRANSACTION_HASH) {
        return null
      }

      return transactionHash
    })
  }

  /**
   * Waits for a Neo X transaction to be mined. `null` means it was still
   * pending when the timeout ran out.
   */
  static async waitForNeoXReceipt(
    transactionHash: string,
    shouldStop: TShouldStop = () => false
  ): Promise<ethers.providers.TransactionReceipt | null> {
    const provider = new ethers.providers.StaticJsonRpcProvider(
      BridgeNetworkHelper.xRpcUrl(),
      {chainId: BridgeNetworkHelper.xChainId(), name: 'neox'}
    )

    return this.poll(this.SOURCE_POLL_INTERVAL_MS, shouldStop, () =>
      provider.getTransactionReceipt(transactionHash)
    )
  }

  /**
   * The nonce the Neo X bridge contract assigned to the withdrawal, read from
   * the withdrawal event of the receipt.
   */
  static withdrawNonceOf(
    receipt: ethers.providers.TransactionReceipt
  ): string | null {
    const bridgeInterface = new ethers.utils.Interface(neoXBridgeABI)

    for (const log of receipt.logs ?? []) {
      try {
        const parsedLog = bridgeInterface.parseLog(log)

        if (
          parsedLog.name === 'NativeWithdrawal' ||
          parsedLog.name === 'TokenWithdrawal'
        ) {
          return parsedLog.args.nonce.toString()
        }
      } catch {
        /* Logs of other contracts (the ERC-20 transfer, for one) don't parse. */
      }
    }

    return null
  }

  /**
   * Waits for the N3 transaction that released the withdrawn funds.
   * `tokenHash` is the NEP-17 script hash on N3, or `null` for native GAS.
   */
  static async waitForN3Release({
    nonce,
    tokenHash,
    shouldStop = () => false,
  }: {
    nonce: string
    tokenHash: string | null
    shouldStop?: TShouldStop
  }): Promise<TN3Release | null> {
    return this.poll(this.TARGET_POLL_INTERVAL_MS, shouldStop, async () => {
      const {data} = await axios.post(BridgeNetworkHelper.n3FuraUrl(), {
        jsonrpc: '2.0',
        method: 'GetBridgeTxByNonce',
        params: {
          ContractHash: BridgeNetworkHelper.n3BridgeScriptHash(),
          TokenHash: tokenHash ?? '',
          Nonce: Number(nonce),
        },
        id: 1,
      })

      const result = data?.result

      if (!result?.txid) {
        return null
      }

      return {transactionHash: result.txid, vmState: result.Vmstate}
    })
  }

  /**
   * Runs `attempt` until it returns something, the caller stops caring, or the
   * timeout is reached. Errors are swallowed: an indexer that is briefly down
   * is not a bridge that failed.
   */
  private static async poll<T>(
    intervalMs: number,
    shouldStop: TShouldStop,
    attempt: () => Promise<T | null>
  ): Promise<T | null> {
    const deadline = Date.now() + this.TIMEOUT_MS

    while (Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, intervalMs))

      if (shouldStop()) {
        return null
      }

      try {
        const result = await attempt()

        if (result) {
          return result
        }
      } catch (error) {
        console.error('[Bridge] tracking attempt failed', error)
      }
    }

    return null
  }
}
